/**
 * LOGGING SERVICE
 * Professional logging with environment-based filtering
 * 
 * Usage:
 *   logger.debug('Detailed info', { data });  // Only in development
 *   logger.info('User logged in', { userId }); // Only in development
 *   logger.warn('Rate limit approaching');     // Only in development
 *   logger.error('API failed', error);         // Always logged + sent to Sentry in production
 */

enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

class Logger {
  private level: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    
    // In production, only log errors
    // In development, log everything
    this.level = this.isDevelopment ? LogLevel.DEBUG : LogLevel.ERROR;
  }

  /**
   * Debug logging - detailed information for troubleshooting
   * Only visible in development
   */
  debug(message: string, ...args: any[]): void {
    if (this.level <= LogLevel.DEBUG) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Info logging - general information about app flow
   * Only visible in development
   */
  info(message: string, ...args: any[]): void {
    if (this.level <= LogLevel.INFO) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Warning logging - potentially harmful situations
   * Only visible in development
   */
  warn(message: string, ...args: any[]): void {
    if (this.level <= LogLevel.WARN) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  /**
   * Error logging - error events
   * Always logged, sent to monitoring in production
   */
  error(message: string, error?: Error | any, ...args: any[]): void {
    if (this.level <= LogLevel.ERROR) {
      console.error(`[ERROR] ${message}`, error, ...args);
      
      // TODO: In production, send to Sentry or other error tracking service
      // if (!this.isDevelopment && window.Sentry) {
      //   window.Sentry.captureException(error, {
      //     extra: { message, args },
      //   });
      // }
    }
  }

  /**
   * Group logging - for collapsible log groups
   * Only in development
   */
  group(label: string, fn: () => void): void {
    if (this.isDevelopment) {
      console.group(label);
      fn();
      console.groupEnd();
    }
  }

  /**
   * Table logging - for structured data
   * Only in development
   */
  table(data: any): void {
    if (this.isDevelopment) {
      console.table(data);
    }
  }

  /**
   * Time logging - for performance measurement
   * Only in development
   */
  time(label: string): void {
    if (this.isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment) {
      console.timeEnd(label);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for dependency injection
export type { Logger };

